import { t, Trans } from "@lingui/macro";
import { IApiEntitySmpAgTemplate } from "@smp/api";
import {
  UiBox,
  UiButton,
  UiButtonBase,
  UiIconSmmplannerLogo,
  UiLink,
  UiTooltip,
  UiTypography,
} from "@smp/ui";
import { dataArraySieve } from "@smp/utils";
import { IconPlus, IconShare, IconUserCircle } from "@tabler/icons-react";
import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ColorPalette } from "@/components/features/polotno_templates/components/template_dialog/color_palette";
import { TAG_BLACKLIST } from "@/components/features/polotno_templates/fixtures";
import { HelperAdapterLink } from "@/components/helpers/helper_adapter_link";
import { useSmpAgTranslation } from "@/hooks/use_smp_ag_translation";
import { useQueryPolotnoTagsAll } from "@/queries/polotno/use_query_polotno_tags_all";
import { customGeneratePath } from "@/utils";

import { TagButton } from "../../../components";
import { useContextPolotnoTemplates } from "../../../context";
import { useStyles } from "./template_info.css";

export const TemplateInfo = ({
  template,
  backUrl,
}: {
  template: IApiEntitySmpAgTemplate;
  backUrl: string;
}) => {
  const classes = useStyles();
  const navigate = useNavigate();
  const getTranslation = useSmpAgTranslation();

  const {
    actions: {
      updateSelectedTags,
      openInEditor,
      copyToEditor,
      setActiveEntity,
    },
    state: { pathPattern, isRoutingDisabled },
  } = useContextPolotnoTemplates();

  const {
    data: { normalized: normalizedTags },
  } = useQueryPolotnoTagsAll();

  const [openCopied, setOpenCopied] = useState(false);

  const templateId = template?.id.toString();

  const fallbackTemplateName = t`Template` + templateId ? templateId : null;

  const colorTags = useMemo(() => {
    const slugs = template?.tags?.filter((tag) => tag.startsWith("color_"));

    return !!slugs?.length && normalizedTags
      ? dataArraySieve(slugs.map((slug) => normalizedTags.get(slug)))
      : undefined;
  }, [normalizedTags, template?.tags]);

  const authorTag = useMemo(() => {
    const slug = template.tags.find((tag) => tag.startsWith("author"));

    return slug && normalizedTags ? normalizedTags?.get(slug) : undefined;
  }, [normalizedTags, template.tags]);

  const typeTag = useMemo(() => {
    const slug = template.tags.find((tag) => tag.startsWith("type"));

    return slug && normalizedTags ? normalizedTags?.get(slug) : undefined;
  }, [normalizedTags, template.tags]);

  const sizeTag = useMemo(() => {
    const slug = template.tags.find((tag) => tag.startsWith("size"));

    return slug && normalizedTags ? normalizedTags?.get(slug) : undefined;
  }, [normalizedTags, template.tags]);

  const isCreatedBySmmplanner =
    !authorTag || authorTag?.slug === "author_smmplanner";

  const handleClickAuthor = () => {
    if (!authorTag || !authorTag.parent) {
      return;
    }

    updateSelectedTags(authorTag.parent, authorTag.slug);

    if (!isRoutingDisabled) {
      navigate(customGeneratePath(pathPattern));
    } else {
      setActiveEntity(undefined);
    }
  };

  const handleTooltipOpen = () => {
    setOpenCopied(true);
  };

  const tooltipTitle = openCopied ? (
    <Trans>Copied to clipboard</Trans>
  ) : (
    <Trans>Share</Trans>
  );

  const handleShare = useCallback(() => {
    handleTooltipOpen();
    navigator.clipboard.writeText(
      `${window.location.origin}/home/templates/${templateId}`,
    );
  }, [templateId]);

  const tagBlacklistFilter = (tag: string) => {
    const tagBlacklist = [...TAG_BLACKLIST];
    return !tagBlacklist.some((prefix) => tag.startsWith(prefix));
  };

  const handleEdit = useCallback(
    (e: MouseEvent) => {
      setActiveEntity(undefined);
      openInEditor(templateId, e.ctrlKey);
    },
    [openInEditor, setActiveEntity, templateId],
  );

  const handleCopy = useCallback(() => {
    if (!copyToEditor) {
      return;
    }

    copyToEditor(template.template_url);
    setActiveEntity(undefined);
  }, [copyToEditor, setActiveEntity, template.template_url]);

  return (
    <UiBox className={classes.wrapper}>
      <UiBox flex={1}>
        <UiTypography
          variant="h3"
          component="h1"
          gutterBottom
          gutterBottomSize="lg"
        >
          {template?.template_name || fallbackTemplateName}
        </UiTypography>

        {template?.description && (
          <UiTypography
            className={classes.description_text}
            gutterBottom
            gutterBottomSize={"lg"}
          >
            {getTranslation(template.description)}
          </UiTypography>
        )}

        <UiBox display="flex" alignItems="center" mb={3}>
          {isCreatedBySmmplanner ? (
            <UiBox className={classes.smmplanner_icon_wrapper}>
              <UiIconSmmplannerLogo className={classes.smmplanner_icon} />
            </UiBox>
          ) : (
            <IconUserCircle className={classes.author_icon} />
          )}
          {authorTag && (
            <UiBox display="flex">
              <UiTypography>
                <Trans>templates.by_user</Trans>{" "}
                <UiLink
                  className={classes.author_link}
                  onClick={handleClickAuthor}
                  component={!isRoutingDisabled ? HelperAdapterLink : undefined}
                  to={!isRoutingDisabled ? backUrl : undefined}
                >
                  {getTranslation(authorTag.translations)}
                </UiLink>
              </UiTypography>
            </UiBox>
          )}
        </UiBox>

        <UiBox mb={2} display="flex" alignItems="center">
          <UiButton
            variant="contained"
            color="primary"
            className={classes.edit_button}
            onClick={handleEdit}
            component={!isRoutingDisabled ? HelperAdapterLink : undefined}
            to={
              !isRoutingDisabled
                ? `/polotno/templates/${template.id}`
                : undefined
            }
          >
            <Trans>templates.modal.button_Edit template</Trans>
          </UiButton>
          <UiTooltip
            PopperProps={{
              disablePortal: true,
            }}
            leaveDelay={500}
            title={tooltipTitle}
          >
            <UiButtonBase
              children={<IconShare />}
              onClick={handleShare}
              className={classes.action_button}
            />
          </UiTooltip>
        </UiBox>

        {copyToEditor && (
          <UiBox mb={2} display="flex" alignItems="center">
            <UiButton
              variant="semifilled"
              onClick={handleCopy}
              startIcon={<IconPlus />}
            >
              <Trans>templates.modal.button_Copy template</Trans>
            </UiButton>
          </UiBox>
        )}
      </UiBox>
      <UiBox marginTop="auto">
        {!!colorTags?.length && (
          <ColorPalette tags={colorTags} backUrl={backUrl} />
        )}

        <UiTypography gutterBottom gutterBottomSize="lg">
          {typeTag && <strong>{getTranslation(typeTag.translations)}</strong>}{" "}
          {sizeTag && <>{sizeTag.slug.replace("size_", "")}px</>}
        </UiTypography>

        <UiBox className={classes.tags_wrapper}>
          {template?.tags?.filter(tagBlacklistFilter).map((slug) => (
            <TagButton key={slug} slug={slug} backUrl={backUrl} />
          ))}
        </UiBox>
      </UiBox>
    </UiBox>
  );
};
